// 0.77 · Junta duas versões do mesmo documento quando dois dispositivos mudaram a mesma aba antes de sincronizar.
// Antes, o último a enviar apagava o que o outro tinha feito. Agora:
//  · listas com "id": junta os itens dos dois lados; o mesmo item nos dois → fica o alterado por último ("alt"),
//    ou o deste dispositivo quando não há data de alteração;
//  · item que só existe no servidor: entra se foi criado depois da última sincronização deste dispositivo
//    (foi criado no outro). Se é mais antigo, foi apagado aqui e continua apagado. Sem data de criação, entra
//    (melhor reaparecer do que sumir);
//  · objetos: junta campo a campo (até 4 níveis); valores simples → fica o deste dispositivo.
type Obj = Record<string, any>;
const ehObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const comId = (a: unknown): a is Obj[] => Array.isArray(a) && a.length > 0 && a.every(x => ehObj(x) && typeof x.id === 'string');
const criadoEm = (x: Obj): string => String(x.criado ?? x.createdAt ?? x.criadoEm ?? '');
const alteradoEm = (x: Obj): string => String(x.alt ?? x.updatedAt ?? x.alterado ?? '');

function lista(local: Obj[], remoto: Obj[], desde: string, nivel: number): Obj[] {
  const doRemoto = new Map(remoto.map(x => [x.id, x]));
  const out: Obj[] = local.map(l => {
    const r = doRemoto.get(l.id);
    if (!r) return l;
    const al = alteradoEm(l), ar = alteradoEm(r);
    if (al && ar && ar > al) return r;
    if (al && ar) return l;
    return juntar(l, r, desde, nivel + 1);
  });
  const temLocal = new Set(local.map(x => x.id));
  remoto.forEach((r, i) => {
    if (temLocal.has(r.id)) return;
    const c = criadoEm(r);
    // Criado antes da última sincronização e ausente aqui = apagado aqui.
    if (c && desde && c.slice(0, c.length >= 19 ? 19 : 10) < desde.slice(0, c.length >= 19 ? 19 : 10)) return;
    // Mantém perto da posição que tinha no servidor.
    const antes = i > 0 ? out.findIndex(x => x.id === remoto[i - 1].id) : -1;
    out.splice(antes >= 0 ? antes + 1 : out.length, 0, r);
  });
  return out;
}

export function juntar(local: any, remoto: any, desde: string, nivel = 0): any {
  if (nivel > 4) return local;
  if (Array.isArray(local) && Array.isArray(remoto)) {
    if ((comId(local) || local.length === 0) && comId(remoto)) return lista(local, remoto, desde, nivel);
    return local;
  }
  if (ehObj(local) && ehObj(remoto)) {
    const out: Obj = { ...remoto, ...local };
    for (const k of Object.keys(local)) if (k in remoto) out[k] = juntar(local[k], remoto[k], desde, nivel + 1);
    return out;
  }
  return local === undefined ? remoto : local;
}
